import React, { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { NumericFormat } from "react-number-format";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useUserData } from "../../hooks";
import { codeFontFamily } from "utils/code-font";

export default function SubscriptionCreditsTable() {
    const {
        lifeTimeTotal,
        lifetimeCredit,
        availableCredit,
        creditsUsed,
        userReferralCode,
        groupReferralCode,
        isReferralHidden,
    } = useUserData();

    const [copyTooltip, setCopyTooltip] = useState(false);
    const referralCode = groupReferralCode ? groupReferralCode : userReferralCode;
    const signupUrl = `${window.location.origin}/signup?referral_code=${referralCode}`;

    const handleCopyReferral = () => {
        navigator.clipboard
            .writeText(signupUrl)
            .then(() => {
                setCopyTooltip(true);
            })
            .catch((error) => {
                console.error("Unable to copy data to clipboard:", error);
            });
    };

    const handleMouseOut = () => {
        setTimeout(() => {
            setCopyTooltip(false);
        }, 1000);
    };

    const creditsRows = [
        {
            id: "referrals",
            name: "Referrals",
            lifeTimeTotal,
            lifetimeCredit,
            creditsUsed,
            availableCredit,
            referralCode,
        },
    ].filter((row) => row.id !== "referrals" || !isReferralHidden);

    if (creditsRows.length === 0) {
        return null;
    }

    const creditsColumns = [
        {
            field: "name",
            headerName: "",
            flex: 2,
            sortable: false,
            renderCell: ({ value }) => <strong>{value}</strong>,
        },
        {
            field: "lifeTimeTotal",
            headerName: "Lifetime Total",
            flex: 2,
            sortable: false,
        },
        {
            field: "lifetimeCredit",
            headerName: "Lifetime Credit",
            flex: 2,
            sortable: false,
            renderCell: ({ value }) => (
                <NumericFormat
                    value={value}
                    decimalScale={2}
                    prefix="$"
                    defaultValue="-"
                    displayType="text"
                    fixedDecimalScale={true}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                />
            ),
        },
        {
            field: "availableCredit",
            headerName: "Available Credit",
            flex: 2,
            sortable: false,
            renderCell: ({ value }) => (
                <NumericFormat
                    value={value}
                    decimalScale={2}
                    prefix="$"
                    defaultValue="-"
                    displayType="text"
                    fixedDecimalScale={true}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                />
            ),
        },

        {
            field: "referralCode",
            headerName: "",
            flex: 3,
            sortable: false,
            renderCell: ({ value }) =>
                !isReferralHidden ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: codeFontFamily,
                                letterSpacing: "0.04em",
                            }}
                        >
                            {value}
                        </Typography>
                        {value && (
                            <Tooltip
                                title={
                                    copyTooltip ? "Copied to clipboard!" : "Copy referral link"
                                }
                            >
                                <IconButton
                                    onClick={handleCopyReferral}
                                    size="small"
                                    onMouseOut={handleMouseOut}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ) : null,
        },
    ];

    return (
        <Box sx={{ pb: 0 }}>
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                Subscription Credits
            </Typography>
            <DataGrid
                columns={creditsColumns}
                rows={creditsRows}
                density="compact"
                autoHeight
                hideFooter
                disableColumnMenu
                disableRowSelectionOnClick
                sx={{
                    border: "none",
                    maxWidth: "100vw",
                    [`& .${gridClasses.cell}`]: {
                        borderBottom: "none",
                    },
                    [`& .${gridClasses.columnHeaders}`]: {
                        borderBottom: "none",
                    },
                    [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                        padding: 0,
                    },
                    [`& .${gridClasses.columnSeparator}`]: {
                        display: "none",
                    },
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                        outline: "none",
                    },
                    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                        outline: "none",
                    },
                }}
            />
        </Box>
    );
}
